import {
  ArrowPathIcon,
  MagnifyingGlassIcon,
} from "@heroicons/react/24/outline";
import { Button, DateRangePicker, Input, Select, SelectItem, Tooltip } from "@heroui/react";
import { CalendarDate } from "@internationalized/date";
import React, { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchActivities, fetchActivityTitles } from "../../services/api";
import { formatTimestamp } from "../../utils/utilMethods";

interface Activity {
  id: number;
  title: string;
  description: string;
  createdAt: string;
}

interface DateRange {
  start: CalendarDate;
  end: CalendarDate;
}

const History: React.FC = () => {
  const [activities, setActivities] = useState<Activity[]>([]);
  const [selectedTitle, setSelectedTitle] = useState<string>("");
  const [selectedDays, setSelectedDays] = useState<number | undefined>();
  const [activityTitles, setActivityTitles] = useState<string[]>([]);
  const [searchText, setSearchText] = useState<string>("");
  const [dateRange, setDateRange] = useState<DateRange | null>(null);

  const { t, i18n } = useTranslation('history');

  const formatAlltext = (text?: string | null): string => {
    if (!text?.trim()) return "-";
    return text
      .split(",")
      .map((word) =>
        word
          .trim()
          .replace(/_/g, " ")
          .toLowerCase()
          .replace(/\b\w/g, (l) => l.toUpperCase())
      )
      .join(", ");
  };

  useEffect(() => {
    const savedLanguage = localStorage.getItem('i18nextLng') || 'en';
    i18n.changeLanguage(savedLanguage);
  }, [i18n]);

  const formatSportName = (text: string) => {
    if (text.startsWith(t("all_sports"))) return text;
    return text
      .replace(/_/g, " ")
      .toLowerCase()
      .replace(/\b\w/g, (l) => l.toUpperCase());
  };

  const parseHtmlContent = (htmlString: string) => {
    const tempDiv = document.createElement('div');
    tempDiv.innerHTML = htmlString;

    const convertNode = (node: Node): React.ReactNode => {
      if (node.nodeType === Node.TEXT_NODE) {
        return node.textContent;
      }

      if (node.nodeType === Node.ELEMENT_NODE) {
        const element = node as Element;
        const tagName = element.tagName.toLowerCase();
        const children = Array.from(element.childNodes).map((child, index) =>
          convertNode(child)
        );

        switch (tagName) {
          case 'strong':
          case 'b':
            return <strong key={Math.random()} className="font-semibold">{children}</strong>;
          case 'em':
          case 'i':
            return <em key={Math.random()} className="italic">{children}</em>;
          case 'u':
            return <u key={Math.random()} className="underline">{children}</u>;
          case 'br':
            return <br key={Math.random()} />;
          case 'p':
            return <p key={Math.random()} className="mb-2">{children}</p>;
          case 'span':
            return <span key={Math.random()}>{children}</span>;
          case 'div':
            return <div key={Math.random()}>{children}</div>;
          case 'h1':
            return <h1 key={Math.random()} className="text-xl font-bold mb-2">{children}</h1>;
          case 'h2':
            return <h2 key={Math.random()} className="text-lg font-bold mb-2">{children}</h2>;
          case 'h3':
            return <h3 key={Math.random()} className="text-base font-bold mb-1">{children}</h3>;
          case 'ul':
            return <ul key={Math.random()} className="list-disc ml-4 mb-2">{children}</ul>;
          case 'ol':
            return <ol key={Math.random()} className="list-decimal ml-4 mb-2">{children}</ol>;
          case 'li':
            return <li key={Math.random()} className="mb-1">{children}</li>;
          case 'a':
            return <a key={Math.random()} className="text-blue-600 underline" href={element.getAttribute('href') || '#'}>{children}</a>;
          default:
            return <span key={Math.random()}>{children}</span>;
        }
      }

      return null;
    };

    return Array.from(tempDiv.childNodes).map((node, index) => convertNode(node));
  };

  const formatDescriptionWithBoldLastWord = (text: string) => {
    const containsHtml = /<[^>]*>/g.test(text);

    if (containsHtml) {
      return (
        <div className="text-sm text-gray-600">
          {parseHtmlContent(text)}
        </div>
      );
    }

    const words = text.trim().split(" ");
    if (words.length === 0) return null;

    let lastWord = words.pop() || "";
    lastWord = lastWord.replace(/^[<>()"'.,]+|[<>()"'.,>]+$/g, "");

    const rest = words.join(" ");
    const formattedSport = formatSportName(lastWord);

    return (
      <span className="text-sm text-gray-600">
        {rest}{" "}
        <span className="font-semibold text-inherit">{formattedSport}</span>
      </span>
    );
  };

  const formatDateToISO = (date: CalendarDate): string => {
    return `${date.year}-${String(date.month).padStart(2, '0')}-${String(date.day).padStart(2, '0')}`;
  };

  useEffect(() => {
    fetchActivityTitles().then(setActivityTitles);
  }, []);

  useEffect(() => {
    const params: any = {
      title: selectedTitle || undefined,
      days: selectedDays,
      description: searchText || undefined,
    };
    if (dateRange) {
      params.startDate = formatDateToISO(dateRange.start);
      params.endDate = formatDateToISO(dateRange.end);
    }

    fetchActivities(params)
      .then(setActivities)
      .catch((err) => console.error("Error loading activity history:", err));
  }, [selectedTitle, selectedDays, searchText, dateRange]);

  const handleDateRangeChange = (range: DateRange | null) => {
    setDateRange(range);
    if (range) {
      setSelectedDays(undefined);
    }
  };


  return (
    <div className="w-full h-full flex flex-col bg-gray-50">
      <div className="sticky top-0 z-10 bg-white border-b border-gray-200 p-2 flex-shrink-0">
        <div className="max-w-full mx-auto">
          <div className="flex items-center gap-3 flex-wrap">
            <Input
              placeholder={t("search_placeholder")}
              value={searchText}
              onChange={(e) => setSearchText(e.target.value)}
              startContent={
                <MagnifyingGlassIcon className="w-4 h-4 text-gray-400" />
              }
              className="w-64 min-w-64"
              size="sm"
            />

            <Select
              placeholder={t("select_title_placeholder")}
              className="w-48 min-w-48"
              size="sm"
              selectedKeys={selectedTitle ? [selectedTitle] : []}
              onChange={(e) => setSelectedTitle(e.target.value)}
            >
              {activityTitles.map((title) => (
                <SelectItem key={title}>{formatAlltext(title)}</SelectItem>
              ))}
            </Select>

            <DateRangePicker
              className="w-64"
              size="sm"
              value={dateRange}
              onChange={handleDateRangeChange}
              showMonthAndYearPickers
              visibleMonths={2}
            />

            <Tooltip content={t("reset_filters")}>
              <Button
                isIconOnly
                variant="light"
                className="h-10 w-10 text-gray-600 hover:text-gray-800"
                size="sm"
                onPress={() => {
                  setSelectedTitle("");
                  setSelectedDays(undefined);
                  setSearchText("");
                  setDateRange(null);
                }}
              >
                <ArrowPathIcon className="w-5 h-5" />
              </Button>
            </Tooltip>
          </div>
        </div>
      </div>

      <div className="p-2 space-y-4">
        <ul className="space-y-3">
          {activities.map((item) => (
            <li key={item.id} className="text-left">
              <div className="bg-white p-3 rounded-lg shadow-sm border border-gray-200">
                <div className="mb-2">
                  {formatDescriptionWithBoldLastWord(item.description)}
                </div>
                <p className="text-xs text-gray-400">
                  {formatTimestamp(item.createdAt)}
                </p>
              </div>
            </li>
          ))}
        </ul>

        {activities.length === 0 && (
          <div className="text-center py-8 text-gray-500">
            <p>{t("no_activities_found")}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default History;
